import { autoUpdate, flip, offset, shift, useDismiss, useFloating, useInteractions } from '@floating-ui/react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { GlobalAccount, GUID, Portfolio, PortfolioType } from '@ndvr/portfolio-api';

import { usePortfolios } from 'views/portfolio/portfolio-queries';
import {
    Card,
    FormLabel,
    HorizontalLayout,
    IconChevron,
    IconClose,
    SearchInput,
    Spinner,
    Text,
    VerticalLayout,
} from 'widgets';

export const usePortfolioSelector = (
    globalAccountGUID: GUID<GlobalAccount>,
    selectedPortfolioGUID?: GUID<Portfolio>,
    excludedPortfolioGUIDs?: GUID<Portfolio>[],
) => {
    const { data: portfolios, isLoading } = usePortfolios(globalAccountGUID, PortfolioType.REAL, true);

    const [selectedPortfolio, setSelectedPortfolio] = useState(
        portfolios?.find((portfolio) => portfolio.portfolioGUID === selectedPortfolioGUID),
    );
    const [searchTerm, setSearchTerm] = useState('');
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (!open) {
            setSearchTerm('');
        }
    }, [open]);

    const reset = useCallback(() => {
        setSelectedPortfolio(undefined);
        setSearchTerm('');
        setOpen(false);
    }, []);

    const { x, y, refs, strategy, context } = useFloating({
        placement: 'bottom-start',
        strategy: 'fixed',
        open,
        onOpenChange: setOpen,
        middleware: [offset(8), flip(), shift({ padding: 8 })],
        whileElementsMounted: autoUpdate,
    });
    useInteractions([useDismiss(context)]);

    const filteredPortfolios = useMemo(() => {
        const result =
            portfolios?.filter((portfolio) => !excludedPortfolioGUIDs?.includes(portfolio.portfolioGUID)) ?? [];
        if (!searchTerm) {
            return result;
        }
        return result.filter((portfolio) =>
            [portfolio.portfolioGUID, portfolio.name.plainText, portfolio.custodyAccountId].some(
                (text) => text && text.toLowerCase().includes(searchTerm.toLowerCase()),
            ),
        );
    }, [excludedPortfolioGUIDs, portfolios, searchTerm]);

    const PortfolioSelector = useCallback(
        ({ disabled, hideLabel, clearable }: { disabled?: boolean; hideLabel?: boolean; clearable?: boolean }) => (
            <>
                <VerticalLayout $gap={8}>
                    {!hideLabel && <FormLabel>Select Account</FormLabel>}
                    <Wrapper ref={refs.setReference}>
                        {open ? (
                            <SearchInput
                                placeholder="Search..."
                                value={searchTerm}
                                onChange={(ev) => setSearchTerm(ev.target.value)}
                                autoFocus
                            />
                        ) : (
                            <StyledSelect
                                $apart
                                $rowCenter
                                onClick={() => !isLoading && setOpen(true)}
                                $disabled={disabled || isLoading}
                            >
                                {selectedPortfolio ? (
                                    <PortfolioItemLabel portfolio={selectedPortfolio} />
                                ) : (
                                    <Text $truncated>Select Account</Text>
                                )}
                                {isLoading ? (
                                    <Spinner size={16} />
                                ) : clearable && selectedPortfolio ? (
                                    <StyledIconClose
                                        size="micro"
                                        onClick={(event) => {
                                            reset();
                                            event.stopPropagation();
                                        }}
                                    />
                                ) : (
                                    <StyledIconChevron size="micro" />
                                )}
                            </StyledSelect>
                        )}
                    </Wrapper>
                    <Text helper $themeColor="subtle">
                        Type to search by name, GUID, or custody account ID.
                    </Text>
                </VerticalLayout>

                {open && (
                    <Dropdown
                        ref={refs.setFloating}
                        style={{
                            position: strategy,
                            top: y ?? 0,
                            left: x ?? 0,
                        }}
                    >
                        {filteredPortfolios.length > 0 ? (
                            filteredPortfolios.map((portfolio) => (
                                <ListItem
                                    key={portfolio.portfolioGUID}
                                    $padding="4px 8px"
                                    onClick={() => {
                                        setSelectedPortfolio(portfolio);
                                        setOpen(false);
                                    }}
                                >
                                    <PortfolioItemLabel portfolio={portfolio} />
                                </ListItem>
                            ))
                        ) : (
                            <VerticalLayout $padding="4px 8px">
                                <Text $italic $themeColor="subtle">
                                    No results found
                                </Text>
                            </VerticalLayout>
                        )}
                    </Dropdown>
                )}
            </>
        ),
        [
            filteredPortfolios,
            isLoading,
            open,
            refs.setFloating,
            refs.setReference,
            reset,
            searchTerm,
            selectedPortfolio,
            strategy,
            x,
            y,
        ],
    );

    return { PortfolioSelector, selectedPortfolio, reset };
};

const PortfolioItemLabel = ({ portfolio }: { portfolio: Portfolio }) => (
    <Text $truncated>
        {portfolio.name.plainText}
        <Text $themeColor="subtle" helper>
            &nbsp;[{portfolio.portfolioGUID}]
        </Text>
    </Text>
);

const ListItem = styled(HorizontalLayout)`
    cursor: pointer;
    &:hover {
        background: ${({ theme }) => theme.container.secondaryBackground};
    }
`;

const StyledIconClose = styled(IconClose)`
    cursor: pointer;
`;

const StyledIconChevron = styled(IconChevron)`
    cursor: pointer;
    transform: rotate(90deg);
`;

const StyledSelect = styled(HorizontalLayout)<{ $disabled: boolean }>`
    height: 35px;
    padding: 0 8px;
    text-overflow: ellipsis;

    border-radius: 3px;
    border: 1px solid ${({ theme }) => theme.border.dark};
    cursor: text;

    ${({ $disabled, theme }) =>
        $disabled &&
        `
        pointer-events: none;
        background: ${theme.intent.disabled};
        `};
`;

const Dropdown = styled(Card)`
    overflow-y: auto;
    max-height: 300px;
    width: 500px;
    box-shadow: 0 0 10px 2px ${({ theme }) => theme.border.light};
    z-index: 5;
    padding: 8px 0;
    display: flex;
    flex-direction: column;
`;

const Wrapper = styled.div`
    overflow: hidden;
`;
